import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Card, Form, Input, Space, Table, Typography, message } from 'antd';
import { LinkOutlined, PlusOutlined, ReloadOutlined, ExportOutlined } from '@ant-design/icons';
import { useLanguage } from '../contexts/LanguageContext';
import { COMMERCE_PAGE_SIZE_OPTIONS } from '../constants/commerceListPagination';

const { Title, Text } = Typography;

const CommerceCatalogAdminPage = () => {
  const { t } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [linking, setLinking] = useState(false);
  const [catalogs, setCatalogs] = useState([]);
  const [businessId, setBusinessId] = useState('');
  const [ownedCatalogs, setOwnedCatalogs] = useState([]);
  const [loadingOwned, setLoadingOwned] = useState(false);
  const [createForm] = Form.useForm();
  const [ownedPage, setOwnedPage] = useState(1);
  const [ownedPageSize, setOwnedPageSize] = useState(10);
  const [linkedPage, setLinkedPage] = useState(1);
  const [linkedPageSize, setLinkedPageSize] = useState(10);
  const [creating, setCreating] = useState(false);

  const fetchWithAuth = useCallback((url, options = {}) => {
    const token = localStorage.getItem('token');
    const headers = { 'Content-Type': 'application/json', ...(options.headers || {}) };
    if (token) headers.Authorization = `Bearer ${token}`;
    return fetch(url, { ...options, headers });
  }, []);

  const loadCatalogs = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchWithAuth('/api/commerce/admin/catalogs');
      if (res.status === 403) {
        const data = await res.json().catch(() => ({}));
        message.error(data?.error || t('commerceCatalogAdmin.noPermission'));
        setCatalogs([]);
        return;
      }
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      setCatalogs(Array.isArray(data) ? data : []);
    } catch (err) {
      message.error(`${t('commerceCatalogAdmin.loadCatalogsFailed')}: ${err.message}`);
      setCatalogs([]);
    } finally {
      setLoading(false);
    }
  }, [fetchWithAuth, t]);

  useEffect(() => {
    loadCatalogs();
  }, [loadCatalogs]);

  // 若登入用戶所屬公司已設定 Meta Business ID，預填至「建立新目錄」與「Business 底下所有目錄」
  useEffect(() => {
    const raw = localStorage.getItem('userInfo');
    const companyId = raw ? (JSON.parse(raw)?.company_id ?? null) : null;
    if (!companyId) return;
    fetchWithAuth(`/api/companies/${companyId}`)
      .then(res => res.ok ? res.json() : null)
      .then(company => {
        const metaId = company?.wA_Meta_Business_ID?.trim();
        if (metaId) {
          setBusinessId(metaId);
          createForm.setFieldsValue({ business_id: metaId });
        }
      })
      .catch(() => {});
  }, [fetchWithAuth]);

  const loadOwnedCatalogs = useCallback(async () => {
    const bid = (businessId || '').trim();
    if (!bid) {
      message.warning(t('commerceCatalogAdmin.pleaseEnterMetaBusinessId'));
      return;
    }
    setLoadingOwned(true);
    try {
      const res = await fetchWithAuth(`/api/commerce/admin/catalogs/owned?business_id=${encodeURIComponent(bid)}`);
      if (res.status === 403) {
        const data = await res.json().catch(() => ({}));
        message.error(data?.error || t('commerceCatalogAdmin.noPermission'));
        return;
      }
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        const msg = data?.metaPermissionHint ? `${data.error || t('commerceCatalogAdmin.loadCatalogsFailed')} — ${data.metaPermissionHint}` : (data?.error || data?.detail || t('commerceCatalogAdmin.loadCatalogsFailed'));
        message.error(msg);
        return;
      }
      const data = await res.json();
      const list = Array.isArray(data) ? data : (data?.data ?? []);
      setOwnedCatalogs(list);
      if (data?.metaPermissionHint) {
        message.info(data.metaPermissionHint);
      } else {
        message.success(t('commerceCatalogAdmin.loadedCount', { count: list.length }));
      }
    } catch (err) {
      message.error(`${t('commerceCatalogAdmin.loadCatalogsFailed')}: ${err.message}`);
      setOwnedCatalogs([]);
    } finally {
      setLoadingOwned(false);
    }
  }, [fetchWithAuth, businessId, t]);

  const onCreateCatalog = async () => {
    try {
      const { name, business_id } = await createForm.validateFields();
      if (!name?.trim() || !business_id?.trim()) return;
      setCreating(true);
      const res = await fetchWithAuth('/api/commerce/admin/catalogs', {
        method: 'POST',
        body: JSON.stringify({ name: name.trim(), businessId: business_id.trim() })
      });
      const data = await res.json().catch(() => ({}));
      if (res.status === 403) {
        message.error(data?.error || t('commerceCatalogAdmin.noPermission'));
        return;
      }
      if (!res.ok) {
        const msg = data?.metaPermissionHint ? `${data.error || t('commerceCatalogAdmin.createFailed')} — ${data.metaPermissionHint}` : (data?.error || data?.detail || t('commerceCatalogAdmin.createFailed'));
        message.error(msg);
        return;
      }
      message.success(t('commerceCatalogAdmin.catalogCreated'));
      createForm.resetFields();
      if (business_id?.trim()) {
        setBusinessId(business_id.trim());
        const r = await fetchWithAuth(`/api/commerce/admin/catalogs/owned?business_id=${encodeURIComponent(business_id.trim())}`);
        if (r.ok) {
          const list = await r.json();
          setOwnedCatalogs(Array.isArray(list) ? list : []);
        }
      }
    } catch (e) {
      if (e?.errorFields) return;
      message.error(e?.message || t('commerceCatalogAdmin.createFailed'));
    } finally {
      setCreating(false);
    }
  };

  const onLinkCatalog = async (catalogId) => {
    const id = catalogId != null ? String(catalogId).trim() : '';
    if (!id) {
      message.warning(t('commerceCatalogAdmin.catalogIdEmpty'));
      return;
    }
    try {
      setLinking(true);
      const res = await fetchWithAuth('/api/commerce/admin/catalogs/link', {
        method: 'POST',
        body: JSON.stringify({ catalog_id: id })
      });
      const data = await res.json().catch(() => ({}));
      if (res.status === 403) {
        message.error(data?.error || t('commerceCatalogAdmin.noPermission'));
        return;
      }
      if (!res.ok) {
        const msg = data?.error === 'WABA_MAX_ONE_CATALOG'
          ? t('commerceCatalogAdmin.wabaMaxOneCatalog')
          : (data?.error || data?.detail || t('commerceCatalogAdmin.linkFailed'));
        message.error(msg);
        return;
      }
      message.success(t('commerceCatalogAdmin.linkSuccess'));
      loadCatalogs();
    } catch (err) {
      message.error(err?.message || t('commerceCatalogAdmin.linkFailed'));
    } finally {
      setLinking(false);
    }
  };

  const META_COMMERCE_URL = 'https://business.facebook.com';

  const ownedPaged = useMemo(() => {
    const start = (ownedPage - 1) * ownedPageSize;
    return ownedCatalogs.slice(start, start + ownedPageSize);
  }, [ownedCatalogs, ownedPage, ownedPageSize]);

  const linkedPaged = useMemo(() => {
    const start = (linkedPage - 1) * linkedPageSize;
    return catalogs.slice(start, start + linkedPageSize);
  }, [catalogs, linkedPage, linkedPageSize]);

  const columns = [
    { title: t('commerceCatalogAdmin.catalogId'), dataIndex: 'id', key: 'id', ellipsis: true, render: (val) => <Text copyable>{val}</Text> },
    { title: t('commerceCatalogAdmin.name'), dataIndex: 'name', key: 'name', ellipsis: true },
    {
      title: t('commerceCatalogAdmin.action'),
      key: 'unlink',
      width: 180,
      render: () => (
        <a href={META_COMMERCE_URL} target="_blank" rel="noopener noreferrer">
          <ExportOutlined /> {t('commerceCatalogAdmin.unlinkAction')}
        </a>
      )
    }
  ];

  return (
    <div style={{ padding: 24 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Space wrap>
          <Button type="primary" icon={<ReloadOutlined />} onClick={loadCatalogs} loading={loading}>
            {t('commerceCatalogAdmin.reload')}
          </Button>
          <Button icon={<ExportOutlined />} href={META_COMMERCE_URL} target="_blank" rel="noopener noreferrer">
            {t('commerceCatalogAdmin.openMetaCommerceManager')}
          </Button>
        </Space>
        <Title level={4} style={{ margin: 0, textAlign: 'right' }}>{t('commerceCatalogAdmin.title')}</Title>
      </div>
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message={t('commerceCatalogAdmin.alertInfo')}
      />
      <Alert
        type="warning"
        showIcon
        style={{ marginBottom: 16 }}
        message={t('commerceCatalogAdmin.alertMetaPermission')}
        description={t('commerceCatalogAdmin.alertMetaPermissionDesc')}
      />

      <Card title={t('commerceCatalogAdmin.cardCreate')} style={{ marginBottom: 16 }}>
        <Form form={createForm} layout="inline" onFinish={onCreateCatalog}>
          <Form.Item name="business_id" label={t('commerceCatalogAdmin.metaBusinessId')} rules={[{ required: true, message: t('commerceCatalogAdmin.required') }]}>
            <Input placeholder="Business ID" style={{ width: 220 }} />
          </Form.Item>
          <Form.Item name="name" label={t('commerceCatalogAdmin.catalogName')} rules={[{ required: true, message: t('commerceCatalogAdmin.required') }]}>
            <Input placeholder={t('commerceCatalogAdmin.catalogNamePlaceholder')} style={{ width: 200 }} />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit" icon={<PlusOutlined />} loading={creating}>
              {t('commerceCatalogAdmin.createCatalog')}
            </Button>
          </Form.Item>
        </Form>
        <Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
          {t('commerceCatalogAdmin.metaBusinessIdHint')}
        </Text>
      </Card>

      <Card title={t('commerceCatalogAdmin.cardOwned')} style={{ marginBottom: 16 }}>
        <Space style={{ marginBottom: 12 }} wrap>
          <Input
            placeholder={t('commerceCatalogAdmin.inputMetaBusinessId')}
            value={businessId}
            onChange={(e) => setBusinessId(e.target.value)}
            style={{ width: 260 }}
          />
          <Button type="primary" onClick={loadOwnedCatalogs} loading={loadingOwned} icon={<ReloadOutlined />}>
            {t('commerceCatalogAdmin.loadCatalogs')}
          </Button>
        </Space>
        <Table
          loading={loadingOwned}
          dataSource={ownedPaged}
          rowKey="id"
          size="small"
          pagination={{
            current: ownedPage,
            pageSize: ownedPageSize,
            total: ownedCatalogs.length,
            showSizeChanger: true,
            pageSizeOptions: COMMERCE_PAGE_SIZE_OPTIONS,
            onChange: (p, ps) => {
              setOwnedPage(p);
              setOwnedPageSize(ps);
            }
          }}
          columns={[
            { title: t('commerceCatalogAdmin.catalogId'), dataIndex: 'id', key: 'id', ellipsis: true, render: (val) => <Text copyable>{val}</Text> },
            { title: t('commerceCatalogAdmin.name'), dataIndex: 'name', key: 'name', ellipsis: true },
            {
              title: t('commerceCatalogAdmin.action'),
              key: 'action',
              render: (_, row) => (
                <Button type="link" size="small" icon={<LinkOutlined />} onClick={() => onLinkCatalog(row.id)} loading={linking}>
                  {t('commerceCatalogAdmin.linkToWaba')}
                </Button>
              )
            }
          ]}
          locale={{ emptyText: t('commerceCatalogAdmin.emptyOwned') }}
        />
      </Card>

      <Card title={t('commerceCatalogAdmin.cardLinked')} style={{ marginBottom: 16 }}>
        <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>{t('commerceCatalogAdmin.unlinkHint')}</Text>
        <Table
          loading={loading}
          dataSource={linkedPaged}
          columns={columns}
          rowKey="id"
          pagination={{
            current: linkedPage,
            pageSize: linkedPageSize,
            total: catalogs.length,
            showSizeChanger: true,
            pageSizeOptions: COMMERCE_PAGE_SIZE_OPTIONS,
            onChange: (p, ps) => {
              setLinkedPage(p);
              setLinkedPageSize(ps);
            }
          }}
          size="small"
          locale={{ emptyText: t('commerceCatalogAdmin.emptyLinked') }}
        />
      </Card>
    </div>
  );
};

export default CommerceCatalogAdminPage;
